<template>
  <div class="card relative flex w-full flex-col items-center bg-secondary p-4">
    <div class="flex w-full items-baseline justify-between gap-4 py-2 text-white">
      <div class="flex flex-row">
        <div class="mr-2 flex h-5 w-5 items-center justify-center rounded-md bg-gray-500">
          <component
            :is="isCollapsed ? IconChevronUp : IconChevronDown"
            class="h-4 w-4 text-white"
          />
        </div>
        <p class="mr-2 text-sm font-semibold">{{ details.receiptCode }}</p>
        <div
          v-if="details.grouping"
          class="mr-2 flex h-6 w-6 items-center justify-center rounded-md bg-blue-500 text-black"
        >
          <IconStack3Filled class="h-5 w-5" />
        </div>
      </div>
      <StatusBadge :color="status.color" :text="status.label"></StatusBadge>
    </div>
    <div class="flex w-full items-baseline justify-between gap-4 py-2 text-white">
      <div class="flex flex-col gap-1">
        <p class="text-xs font-semibold text-gray-400">DESTINATÁRIO</p>
        <p class="text-xs font-semibold">{{ details.cnpjDest }}</p>
      </div>
      <div class="flex flex-col gap-1">
        <p class="text-xs font-semibold text-gray-400">VOLUME</p>
        <p class="text-xs font-semibold">{{ details.totalVolume }}</p>
      </div>
    </div>

    <VButton
      v-if="details.grouping && !isCollapsed"
      class="my-2"
      type="solid"
      title="Iniciar conferência"
      :callback="async () => initCheck(details.barCode1)"
    />

    <!-- Produtos -->
    <div v-if="isCollapsed" class="mt-4 w-full border-t border-gray-600 pt-4">
      <p class="mb-2 text-xs font-bold text-gray-400">PRODUTOS</p>
      <div
        v-for="(product, index) in sortedProducts"
        :key="index"
        class="flex items-center justify-between border-b border-gray-700 py-2 last:border-b-0"
      >
        <ReceiptProductInfo :product="product" :is-selected="product.isSelected"/>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import VButton from '@/components/Button/VButton.vue'
import { STATUS_COLOR } from '@/constraints'
import { EStatus } from '@/enums'
import type { ShippingReceiptStatus } from '@/services/WarrantyApiService/types'
import { IconChevronDown, IconChevronUp, IconStack3Filled } from '@tabler/icons-vue'
import { computed } from 'vue'
import ReceiptProductInfo from '../ReceiptProductInfo/ReceiptProductInfo.vue'
import StatusBadge from '../StatusBadge/StatusBadge.vue'

interface Product {
  id: string | null;
  productId: string;
  productCode: string | null;
  description: string | null;
  volume: number;
  totalChecked: number;
}

interface CarrierTag {
  id: string
  barCode1: string
  barCode2: string | null
  productUnitId: string;
}

export interface ReceiptDetailsProps {
  id: string
  receiptCode: string
  barCode1: string
  barCode2: string
  carrier: string
  cnpjDest: string
  totalVolume: number
  grouping: boolean
  invoiceDate?: Date
  shippingReceiptProducts: Product[]
  shippingReceiptCarrierTags: CarrierTag[]
  status: ShippingReceiptStatus
}

interface ReceiptDetailsCardProps {
  details: ReceiptDetailsProps;
  isCollapsed: boolean;
  onInitCheck?: (barCode: string) => void;
  selectedProductId?: string;
}

const props = defineProps<ReceiptDetailsCardProps>()

const status = computed(() => {
  return {
    label: EStatus[props.details.status],
    color: STATUS_COLOR[EStatus[props.details.status]]
  }
});

const sortedProducts = computed(() => {
  return props.details.shippingReceiptProducts.map(p => {
    return {
      ...p,
      isSelected: p.productId === props.selectedProductId
    }
  }).sort((a, b) => {
    return a.isSelected ? -1 : b.isSelected ? 1 : 0
  })
})

function initCheck(barCode: string) {
  props.onInitCheck ? props.onInitCheck(barCode) : null
}
</script>

<style scoped></style>
